/* eslint-disable no-underscore-dangle */
import "leaflet-ruler";
import "leaflet-ruler/src/leaflet-ruler.css";
import "./LinearRuler.css";

import { Chip, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import L from "leaflet";
import StraightenIcon from "@mui/icons-material/Straighten";

const SYSTEMS = ["metric", "imperial"];

const getUnitConfig = (system) => {
    if (system === "metric") {
        return {
            lengthUnit: {
                display: "m",
                decimal: 2,
                factor: 1000,
                label: "Distance:",
            },
            angleUnit: { display: "°", decimal: 2, label: "Bearing:" },
        };
    }
    return {
        lengthUnit: {
            display: "ft",
            decimal: 2,
            factor: 3280.84,
            label: "Distance:",
        },
        angleUnit: { display: "°", decimal: 2, label: "Bearing:" },
    };
};

export const LinearRuler = ({ map, isActive, setIsActive }) => {
    const theme = useTheme();
    const [system, setSystem] = useState(SYSTEMS[0]);

    useEffect(() => {
        if (!map || !isActive) return;

        const unitConfig = getUnitConfig(system);
        const control = L.control.ruler({
            position: "topright",
            lineStyle: {
                color: theme.palette.primary.main,
                weight: 2,
            },
            circleMarker: {
                color: theme.palette.primary.main,
                radius: 4,
            },
            ...unitConfig,
        });
        control.addTo(map);
        const controlContainer = control.getContainer();
        if (controlContainer) {
            controlContainer.style.display = "none";
        }

        // Disable any active geoman mode before activating the ruler
        map.pm.disableGlobalDragMode();
        map.pm.disableGlobalEditMode();
        map.pm.disableGlobalRotateMode();
        map.pm.disableGlobalRemovalMode();
        map.pm.disableDraw();

        // Programmatically activate the ruler mode
        control._toggleMeasure();

        // _toggleMeasure bound the original _clicked to the map click event.
        // We need to unbind it and bind our patched version instead.
        const originalClickedRef = control._clicked;
        map.off("click", originalClickedRef, control);

        // Remove built-in dblclick/keydown handlers — our patched _clicked
        // handles double-click detection, and _closePath would cause
        // duplicate handler bindings via its _toggleMeasure re-activation.
        L.DomEvent.off(map._container, "dblclick", control._closePath, control);
        L.DomEvent.off(map._container, "keydown", control._escape, control);

        // Initialize _result so _clicked doesn't crash if _moving hasn't
        // run yet (e.g. very fast clicks without mouse movement).
        control._result = { Bearing: 0, Distance: 0 };

        // Reset the current drawing but stay in drawing mode (ready for a new line)
        const resetDrawing = () => {
            // _tempLine/_tempPoint are added directly to the map by _moving
            if (control._tempLine) map.removeLayer(control._tempLine);
            if (control._tempPoint) map.removeLayer(control._tempPoint);
            control._tempLine = null;
            control._tempPoint = null;
            // A single point has no measurement value — remove it
            if (control._clickCount === 1) {
                control._allLayers.removeLayer(control._pointLayer);
            }
            // New permanent layers for the next line (old ones stay visible)
            control._pointLayer = L.featureGroup().addTo(control._allLayers);
            control._polylineLayer = L.featureGroup().addTo(control._allLayers);
            control._clickedLatLong = null;
            control._clickedPoints = [];
            control._totalLength = 0;
            control._clickCount = 0;
        };

        // Patch _clicked to finish the current line when clicking
        // on the last placed point, then start a new one.
        const originalClicked = originalClickedRef.bind(control);

        control._clicked = function (e) {
            if (control._clickedLatLong) {
                const lastPx = map.latLngToContainerPoint(
                    control._clickedLatLong
                );
                const clickPx = map.latLngToContainerPoint(e.latlng);
                if (lastPx.distanceTo(clickPx) < 10) {
                    resetDrawing();
                    return;
                }
            }
            originalClicked(e);
        };

        // Bind our patched _clicked to the map
        map.on("click", control._clicked, control);

        // Make markers and lines non-interactive so they can't be dragged / focused
        control.options.circleMarker = {
            ...control.options.circleMarker,
            interactive: false,
        };
        control.options.lineStyle = {
            ...control.options.lineStyle,
            interactive: false,
        };

        return () => {
            // Remove temp layers that _moving adds directly to the map
            // (these aren't part of _allLayers and won't be cleaned by _toggleMeasure)
            if (control._tempLine) {
                map.removeLayer(control._tempLine);
            }
            if (control._tempPoint) {
                map.removeLayer(control._tempPoint);
            }
            // Deactivate ruler mode before removing control
            if (control._choice) {
                control._toggleMeasure();
            }
            map.removeControl(control);
            /* eslint-enable no-underscore-dangle */
        };
    }, [map, system, theme.palette.primary.main, isActive]);

    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ height: "22px" }}
        >
            <IconButton
                color={isActive ? "primary" : "default"}
                onClick={() => {
                    // Restore drag mode synchronously when manually closing the ruler.
                    // When other tools deactivate the ruler they handle mode switching themselves
                    // so the effect cleanup must NOT call enableGlobalDragMode
                    if (isActive && map) {
                        map.pm.enableGlobalDragMode();
                    }
                    setIsActive(!isActive);
                }}
                size="small"
                sx={{
                    backgroundColor: isActive
                        ? theme.palette.primary.main
                        : "inherit",
                    color: isActive ? "white" : "inherit",
                    "&:hover": {
                        backgroundColor: isActive
                            ? theme.palette.primary.dark
                            : "inherit",
                    },
                }}
            >
                <StraightenIcon fontSize="small" />
            </IconButton>
            {isActive && (
                <>
                    <Typography variant="body2">Linear ruler</Typography>
                    <Chip
                        label={system}
                        clickable
                        color="primary"
                        size="small"
                        onClick={() =>
                            setSystem(
                                system === SYSTEMS[0] ? SYSTEMS[1] : SYSTEMS[0]
                            )
                        }
                    />
                </>
            )}
        </Stack>
    );
};
